import React from 'react';
import { motion } from 'framer-motion';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../common/SafeIcon';

const { FiTarget, FiClock, FiZap, FiShield } = FiIcons;

const QuizCTASection = () => {
  const benefits = [
    {
      icon: FiTarget,
      text: "Identify your specific cognitive performance gaps"
    },
    {
      icon: FiClock,
      text: "Get personalized recommendations in under 5 minutes"
    },
    {
      icon: FiZap,
      text: "Discover your mental training priorities"
    },
    {
      icon: FiShield,
      text: "100% free with no commitment required"
    }
  ];

  return (
    <section className="py-20 bg-gradient-to-br from-accent-light/30 via-white to-accent-purple/20">
      <div className="max-w-6xl mx-auto px-6 lg:px-8">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8 }}
          className="text-center"
        >
          {/* Header */}
          <h2 className="font-playfair text-3xl lg:text-5xl font-bold text-gray-900 mb-6">
            Ready to Discover Your Mental Performance Gaps?
          </h2>
          
          <p className="text-xl text-gray-700 mb-12 max-w-3xl mx-auto leading-relaxed">
            Take our free assessment to uncover which executive functions are holding back your athletic performance.
          </p>

          {/* Benefits Grid */}
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6 mb-12">
            {benefits.map((benefit, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, delay: index * 0.1 }}
                className="bg-white p-6 rounded-xl shadow-sm border border-gray-100 text-center hover:shadow-md transition-all duration-300"
              >
                <div className="bg-accent-magenta/10 w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4">
                  <SafeIcon icon={benefit.icon} className="h-8 w-8 text-accent-magenta" />
                </div>
                
                <p className="text-gray-700 font-medium leading-relaxed">
                  {benefit.text}
                </p>
              </motion.div>
            ))}
          </div>

          {/* Main CTA */}
          <motion.div
            initial={{ opacity: 0, scale: 0.9 }}
            whileInView={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.8 }}
            className="bg-white p-8 lg:p-12 rounded-2xl shadow-lg border border-gray-100 max-w-4xl mx-auto"
          >
            <h3 className="font-playfair text-2xl lg:text-3xl font-bold text-gray-900 mb-6">
              Start Your Elite Performance Journey Today
            </h3>
            
            <p className="text-lg text-gray-700 mb-8 leading-relaxed">
              Join hundreds of athletes who've discovered their cognitive performance gaps and are now training their brains for championship-level success.
            </p>
            
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <a
                href="https://www.wethebillions.com/quiz"
                target="_blank"
                rel="noopener noreferrer"
                className="btn-gold text-lg inline-flex items-center justify-center gap-2 hover:scale-105 transform transition-all duration-200"
              >
                <SafeIcon icon={FiTarget} className="h-6 w-6" />
                Take Free Assessment
              </a>
              
              <button
                onClick={() => {
                  const element = document.getElementById('contact');
                  if (element) element.scrollIntoView({ behavior: 'smooth' });
                }}
                className="btn-secondary text-lg"
              >
                Book Consultation
              </button>
            </div>
            
            <div className="mt-8 flex items-center justify-center gap-8 text-sm text-gray-600">
              <div className="flex items-center gap-2">
                <SafeIcon icon={FiClock} className="h-4 w-4 text-accent-gold" />
                <span>5-minute assessment</span>
              </div>
              <div className="flex items-center gap-2">
                <SafeIcon icon={FiShield} className="h-4 w-4 text-accent-gold" />
                <span>No email required</span>
              </div>
              <div className="flex items-center gap-2">
                <SafeIcon icon={FiZap} className="h-4 w-4 text-accent-gold" />
                <span>Instant results</span>
              </div>
            </div>
          </motion.div>
        </motion.div>
      </div>
    </section>
  );
};

export default QuizCTASection;